import { Component, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from 'antd';
import { UndoOutlined, DeleteOutlined } from '@ant-design/icons';
import { YMaps, Map, Polygon, Placemark } from '@pbe/react-yandex-maps';
import {
  pathIntersectsPolygon,
  isPointInPolygon,
  isValidPolygonPoint,
  normalizePolygonPoints,
  type PolygonPoint,
} from './geometry';

export type { PolygonPoint };

/** Minimal subset of the Yandex map instance API that we drive imperatively. */
interface YMapInstance {
  setBounds: (bounds: number[][], options?: Record<string, unknown>) => Promise<void>;
  setCenter: (center: number[], zoom?: number, options?: Record<string, unknown>) => Promise<void>;
  container: { fitToViewport: () => void };
}

/** A read-only polygon shown underneath the editable one (e.g. already-saved zones). */
export interface ReferencePolygon {
  id?: number | string;
  name?: string;
  points: PolygonPoint[];
}

interface YMapEvent {
  get: (key: string) => unknown;
}

interface MapPolygonPickerProps {
  /** Current polygon vertices as `[lat, lng]` tuples. */
  value?: PolygonPoint[];
  zoom?: number;
  className?: string;
  /** When false the polygon is shown read-only (no editing / pointer events). */
  selectable?: boolean;
  /** Fallback center used when the polygon is empty. Defaults to Tashkent. */
  defaultCenter?: [number, number];
  /** Previously saved polygons rendered read-only for context. */
  referencePolygons?: ReferencePolygon[];
  /**
   * Optional containing polygon. When set, every vertex must fall inside it;
   * points placed/dragged outside are rejected. Rendered read-only for context.
   */
  boundaryPolygon?: PolygonPoint[];
  undoText?: string;
  clearText?: string;
  onChange?: (points: PolygonPoint[]) => void;
  /** Called when a point is rejected because it falls inside a reference polygon. */
  onRejectPoint?: () => void;
}

const DEFAULT_CENTER: [number, number] = [41.311081, 69.240562];

class MapErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='flex h-full w-full items-center justify-center bg-[#f5f5f5] text-sm text-(--color-text-secondary)'>
          Map failed to load
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Yandex-map polygon picker (via @pbe/react-yandex-maps, no API key required).
 * - Click the map to append a vertex; drag any vertex marker to move it.
 * - "Undo" removes the last vertex, "Clear" removes them all.
 * - The polygon outline renders once there are at least three vertices.
 *
 * Coordinates are kept as `[lat, lng]` which matches both the Yandex geometry
 * order and the API payload, so no conversion is needed by callers.
 */
export function MapPolygonPicker({
  value = [],
  zoom = 11,
  className,
  selectable = true,
  defaultCenter = DEFAULT_CENTER,
  referencePolygons = [],
  boundaryPolygon,
  undoText = 'Undo',
  clearText = 'Clear',
  onChange,
  onRejectPoint,
}: MapPolygonPickerProps) {
  // Bumped when a drag is rejected, to force the affected marker to remount and
  // snap back to its controlled position.
  const [dragResetKey, setDragResetKey] = useState(0);
  const [isMapReady, setIsMapReady] = useState(false);

  const mapInstanceRef = useRef<YMapInstance | null>(null);
  // Tracks whether we still owe an automatic recenter. `defaultState` is only
  // read once at mount, so an initial polygon that arrives afterwards (e.g. when
  // an edit form finishes loading) would otherwise never be brought into view.
  const autoFitRef = useRef<'pending' | 'done' | 'disabled'>('pending');

  const points = normalizePolygonPoints(value);
  const boundaryPoints = normalizePolygonPoints(boundaryPolygon);
  const hasBoundary = boundaryPoints.length >= 3;

  const referenceRings = referencePolygons
    .map((ref) => ({
      id: ref.id,
      name: ref.name,
      points: normalizePolygonPoints(ref.points),
    }))
    .filter((ref) => ref.points.length >= 2);

  const firstReferencePoint = referenceRings[0]?.points[0];
  const center: [number, number] =
    points.length > 0 ? points[0] : hasBoundary ? boundaryPoints[0] : (firstReferencePoint ?? defaultCenter);

  const defaultState = selectable
    ? { center, zoom, controls: [] }
    : { center, zoom, controls: [], behaviors: [] };

  const handleInstance = useCallback((instance: unknown) => {
    const next = (instance as YMapInstance) ?? null;
    mapInstanceRef.current = next;
    setIsMapReady((ready) => {
      const shouldBeReady = Boolean(next);
      return ready === shouldBeReady ? ready : shouldBeReady;
    });
  }, []);

  // A candidate polygon is invalid when it overlaps any existing zone.
  const overlapsReference = (candidate: PolygonPoint[]) =>
    referenceRings.some((ref) => pathIntersectsPolygon(candidate, ref.points, candidate.length >= 3));

  // A single vertex is invalid when it falls outside the containing boundary.
  const outsideBoundary = (point: PolygonPoint) =>
    hasBoundary && !isPointInPolygon(point, boundaryPoints);

  const handleMapClick = (e: YMapEvent) => {
    if (!selectable) return;
    const coords = e.get('coords') as PolygonPoint | undefined;
    if (!coords || !isValidPolygonPoint(coords)) return;
    const point: PolygonPoint = [Number(coords[0]), Number(coords[1])];
    const candidate = [...points, point];
    if (outsideBoundary(point) || overlapsReference(candidate)) {
      onRejectPoint?.();
      return;
    }
    onChange?.(candidate);
  };

  const handleVertexDragEnd = (index: number) => (e: YMapEvent) => {
    if (!selectable) return;
    const target = e.get('target') as { geometry?: { getCoordinates?: () => PolygonPoint } } | undefined;
    const coords = target?.geometry?.getCoordinates?.();
    if (!coords || !isValidPolygonPoint(coords)) return;
    const point: PolygonPoint = [Number(coords[0]), Number(coords[1])];
    const next = points.map((p, i) => (i === index ? point : p));
    if (outsideBoundary(point) || overlapsReference(next)) {
      // Reject the move: remount the markers so the dragged one snaps back.
      onRejectPoint?.();
      setDragResetKey((k) => k + 1);
      return;
    }
    onChange?.(next);
  };

  const handleUndo = () => onChange?.(points.slice(0, -1));
  const handleClear = () => onChange?.([]);

  // Force Yandex to recompute the container size. Without this the tiles can
  // stay blank when the map mounts inside a container that was just revealed
  // (for instance right after an edit form swaps out its loading spinner).
  useEffect(() => {
    if (!isMapReady) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    map.container.fitToViewport();
    const frame = requestAnimationFrame(() => map.container.fitToViewport());
    const timer = window.setTimeout(() => map.container.fitToViewport(), 150);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [isMapReady]);

  // Recenter onto the initial polygon, or onto occupied zones when the map is
  // still empty so previously saved polygons are actually in view.
  useEffect(() => {
    if (autoFitRef.current !== 'pending' || !isMapReady) return;

    if (points.length === 1) {
      autoFitRef.current = 'disabled';
      return;
    }

    const contextPoints =
      points.length >= 2
        ? points
        : [...boundaryPoints, ...referenceRings.flatMap((ref) => ref.points)];

    if (contextPoints.length < 2) return;

    const map = mapInstanceRef.current;
    if (!map) return;

    const lats = contextPoints.map((p) => p[0]);
    const lngs = contextPoints.map((p) => p[1]);
    const minLat = Math.min(...lats);
    const minLng = Math.min(...lngs);
    const maxLat = Math.max(...lats);
    const maxLng = Math.max(...lngs);
    if (![minLat, minLng, maxLat, maxLng].every(Number.isFinite)) return;

    autoFitRef.current = 'done';
    map
      .setBounds(
        [
          [minLat, minLng],
          [maxLat, maxLng],
        ],
        { checkZoomRange: true, zoomMargin: 48 }
      )
      .catch(() => {});
  }, [isMapReady, points, boundaryPoints, referenceRings]);

  return (
    <div className={`relative ${className ?? ''}`}>
      <MapErrorBoundary>
        <YMaps>
          <Map
            instanceRef={handleInstance}
            defaultState={defaultState}
            width='100%'
            height='100%'
            onClick={handleMapClick}
            options={{ suppressMapOpenBlock: true, yandexMapDisablePoiInteractivity: true }}
          >
            {hasBoundary && (
              <Polygon
                geometry={[boundaryPoints]}
                options={{
                  fillColor: '#22c55e33',
                  strokeColor: '#16a34a',
                  strokeWidth: 2,
                  strokeStyle: 'shortdash',
                  interactivityModel: 'default#transparent',
                }}
              />
            )}

            {referenceRings.map((ref, index) => (
              <Polygon
                key={`ref-${ref.id ?? index}`}
                geometry={[ref.points]}
                properties={ref.name ? { hintContent: ref.name } : undefined}
                options={{
                  fillColor: '#ef444466',
                  strokeColor: '#dc2626',
                  strokeWidth: 3,
                  strokeStyle: 'solid',
                  interactivityModel: 'default#transparent',
                }}
              />
            ))}

            {points.length >= 2 && (
              <Polygon
                geometry={[points]}
                options={{
                  fillColor: '#7c3aed33',
                  strokeColor: '#7c3aed',
                  strokeWidth: 2,
                  interactivityModel: 'default#transparent',
                }}
              />
            )}

            {selectable &&
              points.map((point, index) => (
                <Placemark
                  key={`${dragResetKey}-${index}-${point[0]}-${point[1]}`}
                  geometry={point}
                  options={{ draggable: true, preset: 'islands#violetDotIcon' }}
                  properties={{ iconContent: index + 1 }}
                  onDragEnd={handleVertexDragEnd(index)}
                />
              ))}
          </Map>
        </YMaps>
      </MapErrorBoundary>

      {selectable && points.length > 0 && (
        <div className='absolute right-2 top-2 z-2 flex gap-2'>
          <Button size='small' icon={<UndoOutlined />} onClick={handleUndo}>
            {undoText}
          </Button>
          <Button size='small' danger icon={<DeleteOutlined />} onClick={handleClear}>
            {clearText}
          </Button>
        </div>
      )}

      {!selectable && <div className='absolute inset-0 z-1' aria-hidden />}
    </div>
  );
}
